import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Bed, Bath, Users, ArrowRight, Star, Download, ChevronDown, MessageCircle, Home, Search, Send, BookOpen, Calculator, Eye } from 'lucide-react';

import MainLayout from '../../components/layout/MainLayout';

/* ── Color tokens ── */
const C = {
  amber:      '#d97706',
  amberLight: '#fef3c7',
  amberDark:  '#92400e',
  amberHover: '#b45309',
  white:      '#ffffff',
  gray50:     '#f9fafb',
  gray100:    '#f3f4f6',
  gray200:    '#e5e7eb',
  gray400:    '#9ca3af',
  gray500:    '#6b7280',
  gray600:    '#4b5563',
  gray700:    '#374151',
  gray900:    '#111827',
  stone50:    '#fafaf9',
};

/* ── useInView hook ── */
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

/* ── Animated wrapper ── */
const Anim = ({ children, delay = 0, dir = 'up', style = {} }) => {
  const [ref, inView] = useInView();
  const from = {
    up:    'translateY(50px)',
    left:  'translateX(-50px)',
    right: 'translateX(50px)',
    scale: 'scale(0.88)',
  };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : from[dir],
      transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* ── CountUp ── */
const CountUp = ({ end, suffix = '' }) => {
  const [n, setN] = useState(0);
  const [ref, inView] = useInView();
  const done = useRef(false);
  useEffect(() => {
    if (!inView || done.current) return;
    done.current = true;
    const t0 = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - t0) / 2000, 1);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * end));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView]);
  return <span ref={ref}>{n}{suffix}</span>;
};

/* ── Floating Orb ── */
const Orb = ({ size, color, top, left, delay = 0 }) => (
  <div style={{
    position: 'absolute', width: size, height: size, borderRadius: '50%',
    background: color, top, left, filter: 'blur(70px)', opacity: 0.3,
    animation: `floatOrb ${7 + delay}s ease-in-out infinite alternate`,
    animationDelay: `${delay}s`, pointerEvents: 'none', zIndex: 0,
  }} />
);

/* ── Primary Button ── */
const BtnPrimary = ({ onClick, children, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '13px 26px', borderRadius: 50, border: 'none', cursor: 'pointer',
        backgroundColor: hov ? C.amberHover : C.amber,
        color: C.white, fontWeight: 600, fontSize: 15,
        letterSpacing: '0.02em', fontFamily: 'inherit',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hov
          ? '0 14px 32px rgba(217,119,6,0.48)'
          : '0 4px 16px rgba(217,119,6,0.32)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        position: 'relative', overflow: 'hidden',
        ...style,
      }}
    >
      {children}
    </button>
  );
};

/* ── Outline Button ── */
const BtnOutline = ({ onClick, children, dark = false, style = {} }) => {
  const [hov, setHov] = useState(false);
  const borderColor = dark ? 'rgba(255,255,255,0.5)' : C.amber;
  const textColor   = dark ? C.white : C.amber;
  const bgHov       = dark ? 'rgba(255,255,255,0.1)' : 'rgba(217,119,6,0.07)';
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '12px 26px', borderRadius: 50, cursor: 'pointer',
        backgroundColor: hov ? bgHov : 'transparent',
        color: textColor,
        border: `2px solid ${borderColor}`,
        fontWeight: 600, fontSize: 15,
        letterSpacing: '0.02em', fontFamily: 'inherit',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        ...style,
      }}
    >
      {children}
    </button>
  );
};

/* ── Section Label ── */
const SectionLabel = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', marginBottom: 18 }}>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, ${C.amber}, #f59e0b)`, borderRadius: 2 }} />
    <span style={{ fontSize: 12, color: C.amber, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{text}</span>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, #f59e0b, ${C.amber})`, borderRadius: 2 }} />
  </div>
);

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
const API_BASE_URL = process.env.REACT_APP_API_URL;

const HomePage = () => {
  const [featuredVillas, setFeaturedVillas] = useState([]);
  const [services, setServices]             = useState([]);
  const [priceFilter, setPriceFilter]       = useState('daily');
  const [loading, setLoading]               = useState(true);
  const [mousePos, setMousePos]             = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY]               = useState(0);
  const [hoveredVilla, setHoveredVilla]     = useState(null);
  const [hoveredDest, setHoveredDest]       = useState(null);
  const videoRef = useRef(null);

  const destinations = [
    { id: 8, name: 'Bukit',    desc: 'Clifftop views',      image: '/images/bukit.jpg',     emoji: '🏔️' },
    { id: 2, name: 'Canggu',   desc: 'Surfer paradise',     image: '/images/canggu.jpeg',   emoji: '🏄' },
    { id: 5, name: 'Sanur',    desc: 'Quiet beach town',    image: '/images/sanur.jpg',     emoji: '🌅' },
    { id: 3, name: 'Seminyak', desc: 'Upscale beach area',  image: '/images/seminyak.jpeg', emoji: '🌴' },
    { id: 1, name: 'Ubud',     desc: 'Cultural heart',      image: '/images/ubud.jpg',      emoji: '🌿' },
  ];

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    const onMouse  = e  => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouse, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMouse);
    };
  }, []);

  useEffect(() => { fetchVillas(); fetchServices(); }, []);

  const fetchVillas = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/properties/featured`);
      const d = await r.json();
      setFeaturedVillas((d.properties || []).slice(0, 3));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchServices = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/services`);
      const d = await r.json();
      setServices((d.data?.services || []).slice(0, 3));
    } catch (e) { console.error(e); }
  };

  const formatPrice = (villa) => {
    const map = {
      daily:   [villa.daily_price_idr,   'day'],
      monthly: [villa.monthly_price_idr, 'month'],
      yearly:  [villa.yearly_price_idr,  'year'],
    };
    const [price, period] = map[priceFilter] || map.daily;
    return { amount: new Intl.NumberFormat('id-ID').format(price), period, currency: 'IDR' };
  };

  const downloadKit = () =>
    window.open('https://drive.google.com/file/d/1E4_9qkm_mSmf0wWDd5T9NfheukvA4Zvl/view', '_blank');

  const heroOpacity  = Math.max(0, 1 - scrollY / 600);
  const heroParallax = scrollY * 0.35;

  return (
  <MainLayout>
    <div style={{ minHeight: '100vh', backgroundColor: C.white, overflowX: 'hidden', fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── Global CSS ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }

        @keyframes floatOrb {
          0%   { transform: translateY(0px)   scale(1);   }
          100% { transform: translateY(-40px) scale(1.1); }
        }
        @keyframes heroIn {
          from { opacity: 0; transform: translateY(40px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes pulseDot {
          0%, 100% { box-shadow: 0 0 0 0   rgba(217,119,6,0.6); }
          50%      { box-shadow: 0 0 0 10px rgba(217,119,6,0);   }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0px);  opacity: 1;   }
          50%      { transform: translateY(10px); opacity: 0.4; }
        }
        @keyframes floatCard {
          0%, 100% { transform: translateY(0px)   rotate(-1deg); }
          50%      { transform: translateY(-14px)  rotate(1deg);  }
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .hero-1 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.2s both; }
        .hero-2 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.5s both; }
        .hero-3 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.8s both; }

        .shimmer-text {
          background: linear-gradient(90deg, #d97706, #f59e0b, #d97706);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }

        .villa-card {
          transition: transform 0.5s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s ease;
        }
        .villa-card:hover {
          transform: translateY(-14px) scale(1.02) !important;
          box-shadow: 0 40px 80px rgba(0,0,0,0.16) !important;
        }

        .dest-card { transition: transform 0.6s cubic-bezier(0.34,1.56,0.64,1); overflow: hidden; }
        .dest-card:hover { transform: translateY(-10px) scale(1.03); }
        .dest-img { transition: transform 0.8s cubic-bezier(0.16,1,0.3,1); }
        .dest-card:hover .dest-img { transform: scale(1.14); }

        .service-card {
          transition: transform 0.5s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s ease;
        }
        .service-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 30px 60px rgba(0,0,0,0.13) !important;
        }
        .svc-img { transition: transform 0.7s ease; }
        .service-card:hover .svc-img { transform: scale(1.08); }

        .feat-row {
          transition: transform 0.3s ease, background 0.3s ease;
          border-radius: 10px; padding: 10px 12px; margin: -4px -12px;
        }
        .feat-row:hover { transform: translateX(10px); background: rgba(217,119,6,0.06); }

        .why-card { transition: background 0.3s ease; border-radius: 16px; padding: 32px; }
        .why-card:hover { background: rgba(217,119,6,0.05); }
        .why-icon { transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1); }
        .why-card:hover .why-icon { transform: scale(1.25) rotate(-6deg); }

        .scroll-bounce { animation: bounce 2s ease-in-out infinite; }
        .stat-float    { animation: floatCard 6s ease-in-out infinite; }

        .cursor-glow {
          pointer-events: none; position: fixed;
          width: 280px; height: 280px; border-radius: 50%;
          background: radial-gradient(circle, rgba(217,119,6,0.07) 0%, transparent 70%);
          transform: translate(-50%, -50%);
          transition: left 0.12s ease, top 0.12s ease;
          z-index: 9999; mix-blend-mode: multiply;
        }
      `}</style>

      {/* Cursor glow */}
      <div className="cursor-glow" style={{ left: mousePos.x, top: mousePos.y }} />



      {/* ══ HERO ══ */}
      <section style={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
        <video ref={videoRef} autoPlay muted loop playsInline style={{
          width: '100%', height: '100%', objectFit: 'cover',
          transform: `translateY(${heroParallax}px)`, willChange: 'transform',
        }}>
          <source src="/images/hero-video.mp4" type="video/mp4" />
          <img src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=1920&q=80"
            alt="Luxury Villa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </video>

        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.45), rgba(0,0,0,0.72))' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.25), transparent)' }} />
        <Orb size="400px" color="radial-gradient(circle, #d97706, #f59e0b)" top="8%"  left="68%" delay={0} />
        <Orb size="280px" color="radial-gradient(circle, #92400e, #d97706)" top="58%" left="-4%" delay={2} />

        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', padding: '0 24px',
          transform: `translateY(${heroParallax * 0.4}px)`, opacity: heroOpacity,
        }}>
          <div className="hero-1" style={{ marginBottom: 28 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '10px 22px',
              backgroundColor: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.25)', borderRadius: 50,
              color: C.white, fontSize: 12, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: C.amber, animation: 'pulseDot 2s ease-in-out infinite' }} />
              Bali's Premier Villa Management
            </span>
          </div>

          <h1 className="hero-2" style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(44px, 9vw, 92px)', fontWeight: 700,
            color: C.white, lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: 22,
          }}>
            The New Standard<br />
            <span className="shimmer-text">of Bali Hospitality</span>
          </h1>

          <p className="hero-2" style={{
            fontSize: 'clamp(16px, 2.2vw, 21px)', color: 'rgba(255,255,255,0.85)',
            maxWidth: 600, marginBottom: 44, fontWeight: 300, lineHeight: 1.65,
          }}>
            Experience luxury villa management and unforgettable stays in Bali's most stunning locations
          </p>

          <div className="hero-3" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
            <BtnPrimary onClick={() => document.getElementById('villas').scrollIntoView({ behavior: 'smooth' })} style={{ padding: '15px 30px', fontSize: 16 }}>
              <Search size={18} /> Browse Villas
            </BtnPrimary>
            <BtnOutline onClick={downloadKit} dark style={{ padding: '14px 30px', fontSize: 16 }}>
              <Download size={18} /> Download Marketing Kit
            </BtnOutline>
          </div>
        </div>

        <div className="scroll-bounce" style={{
          position: 'absolute', bottom: 36, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          color: 'rgba(255,255,255,0.65)',
        }}>
          <span style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Scroll</span>
          <ChevronDown size={18} />
        </div>
      </section>

      {/* ══ STATS STRIP ══ */}
      <section style={{ padding: '60px 24px', backgroundColor: C.gray900, position: 'relative', overflow: 'hidden' }}>
        <Orb size="200px" color="radial-gradient(circle, rgba(217,119,6,0.3), transparent)" top="-50%" left="30%" />
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px,1fr))', gap: 32, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {[
            { end: 50, suffix: '+', label: 'Premium Villas' },
            { end: 95, suffix: '%', label: 'Guest Satisfaction' },
            { end: 40, suffix: '%', label: 'Higher Occupancy' },
            { end: 15, suffix: '+', label: 'Booking Platforms' },
          ].map((s, i) => (
            <Anim key={i} delay={i * 100}>
              <div className="shimmer-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 52, fontWeight: 700, marginBottom: 6 }}>
                <CountUp end={s.end} suffix={s.suffix} />
              </div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase' }}>{s.label}</div>
            </Anim>
          ))}
        </div>
      </section>

      {/* ══ ABOUT ══ */}
      <section style={{ padding: '112px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="500px" color="radial-gradient(circle, rgba(251,191,36,0.1), transparent)" top="0%" left="-8%" delay={3} />
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px,1fr))', gap: 80, alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <div>
            <Anim dir="left" delay={0}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
                <div style={{ width: 40, height: 3, backgroundColor: C.amber, borderRadius: 2 }} />
                <span style={{ padding: '6px 16px', backgroundColor: C.amberLight, color: C.amberDark, borderRadius: 50, fontSize: 13, fontWeight: 700 }}>🏝️ Local Balinese Company</span>
              </div>
            </Anim>
            <Anim dir="left" delay={100}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(34px,5vw,54px)', fontWeight: 700, color: C.gray900, lineHeight: 1.1, marginBottom: 22 }}>
                Professional Villa<br /><span style={{ color: C.amber }}>Management</span><br />in Bali
              </h2>
            </Anim>
            <Anim dir="left" delay={180}>
              <p style={{ fontSize: 16, color: C.gray600, lineHeight: 1.8, marginBottom: 12 }}>
                Dewanta Property is a local Balinese company specializing in premium villa management. We combine authentic hospitality with modern professional standards to deliver exceptional experiences.
              </p>
              <p style={{ fontSize: 15, color: C.gray500, lineHeight: 1.8, marginBottom: 30 }}>
                As a fully local brand, we understand Bali's unique culture, regulations, and guest expectations—delivering authentic experiences with international standards.
              </p>
            </Anim>
            <Anim dir="left" delay={240}>
              <div style={{ marginBottom: 34 }}>
                {[
                  { label: 'Friendly Service', desc: 'Personal touch in every interaction' },
                  { label: 'Comfort',          desc: 'Creating memorable stays for every guest' },
                  { label: 'Trust',            desc: 'Reliable partnership for property owners' },
                ].map((item, i) => (
                  <div key={i} className="feat-row" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, cursor: 'default' }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: C.amber, flexShrink: 0 }} />
                    <span style={{ color: C.gray700, fontSize: 15 }}><strong>{item.label}</strong> — {item.desc}</span>
                  </div>
                ))}
              </div>
            </Anim>
            <Anim dir="left" delay={320}>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <BtnPrimary onClick={() => window.location.href = '/business'}>
                  <Calculator size={17} /> Calculate Your ROI
                </BtnPrimary>
                <BtnOutline onClick={() => window.location.href = '/contact'}>
                  <MessageCircle size={17} /> Contact Us
                </BtnOutline>
              </div>
            </Anim>
          </div>

          <div style={{ position: 'relative' }}>
            <Anim dir="right" delay={200}>
              <div style={{ borderRadius: 24, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.18)' }}>
                <img src="/images/atas.jpg" alt="Villa Management" style={{ width: '100%', display: 'block', objectFit: 'cover', transition: 'transform 0.8s ease' }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
              </div>
              <div style={{ position: 'absolute', bottom: -8, right: -8, width: 130, height: 130, backgroundColor: C.amber, borderRadius: 20, zIndex: -1, opacity: 0.2 }} />
            </Anim>
            <Anim dir="scale" delay={440}>
              <div className="stat-float" style={{
                position: 'absolute', bottom: -36, left: -36,
                backgroundColor: C.white, borderRadius: 18,
                boxShadow: '0 20px 50px rgba(0,0,0,0.12)',
                padding: '20px 22px', border: `1px solid ${C.gray100}`,
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{ width: 54, height: 54, borderRadius: '50%', backgroundColor: C.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 19, color: C.amber }}>50+</span>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Managed Villas</div>
                  <div style={{ fontWeight: 600, color: C.gray900, fontSize: 15 }}>Premium Properties</div>
                </div>
              </div>
            </Anim>
          </div>
        </div>
      </section>

      {/* ══ FEATURED VILLAS ══ */}
      <section id="villas" style={{ padding: '112px 24px', backgroundColor: C.stone50, position: 'relative', overflow: 'hidden' }}>
        <Orb size="400px" color="radial-gradient(circle, rgba(217,119,6,0.09), transparent)" top="0%" left="78%" delay={2} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>

          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <SectionLabel text="Our Collection" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(30px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 10 }}>
                Featured Luxury Villas
              </h2>
              <p style={{ fontSize: 17, color: C.gray500 }}>Handpicked properties for your perfect Bali getaway</p>
            </div>
          </Anim>

          {/* Price filter */}
          <Anim delay={80}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 52 }}>
              <div style={{ display: 'inline-flex', backgroundColor: C.white, border: `1px solid ${C.gray200}`, borderRadius: 50, padding: 6, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                {['daily', 'monthly', 'yearly'].map(tab => (
                  <button key={tab} onClick={() => setPriceFilter(tab)} style={{
                    padding: '10px 26px', borderRadius: 50, border: 'none', cursor: 'pointer',
                    backgroundColor: priceFilter === tab ? C.amber : 'transparent',
                    color: priceFilter === tab ? C.white : C.gray600,
                    fontWeight: 600, fontSize: 14,
                    textTransform: 'capitalize', fontFamily: 'inherit',
                    boxShadow: priceFilter === tab ? '0 4px 14px rgba(217,119,6,0.38)' : 'none',
                    transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
                  }}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </Anim>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}>
              <div style={{ width: 50, height: 50, border: `4px solid ${C.amberLight}`, borderTopColor: C.amber, borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px,1fr))', gap: 32 }}>
              {featuredVillas.map((villa, i) => {
                const price = formatPrice(villa);
                const hov   = hoveredVilla === villa.id;
                return (
                  <Anim key={villa.id} delay={i * 140}>
                    <div className="villa-card" onClick={() => window.location.href = `/villas/${villa.id}`}
                      onMouseEnter={() => setHoveredVilla(villa.id)}
                      onMouseLeave={() => setHoveredVilla(null)}
                      style={{ backgroundColor: C.white, borderRadius: 20, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', cursor: 'pointer' }}>

                      <div style={{ position: 'relative', height: 256, overflow: 'hidden' }}>
                        <img src={villa.primary_image} alt={villa.title} style={{
                          width: '100%', height: '100%', objectFit: 'cover',
                          transform: hov ? 'scale(1.1)' : 'scale(1)',
                          transition: 'transform 0.8s cubic-bezier(0.16,1,0.3,1)',
                        }} />
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)', opacity: hov ? 1 : 0, transition: 'opacity 0.5s' }} />
                        <span style={{ position: 'absolute', top: 14, left: 14, padding: '5px 14px', backgroundColor: C.amber, color: C.white, borderRadius: 50, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Featured</span>
                        <span style={{ position: 'absolute', top: 14, right: 14, padding: '5px 12px', backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: 50, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Star size={13} fill="#fbbf24" color="#fbbf24" /> 5.0
                        </span>
                        <div style={{ position: 'absolute', bottom: 18, left: 0, right: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, color: C.white, fontWeight: 600, fontSize: 14, opacity: hov ? 1 : 0, transform: hov ? 'translateY(0)' : 'translateY(16px)', transition: 'all 0.4s ease' }}>
                          View Details <ArrowRight size={14} />
                        </div>
                      </div>

                      <div style={{ padding: 24 }}>
                        <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: C.gray900, marginBottom: 4 }}>{villa.title}</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.gray500, fontSize: 13, marginBottom: 12 }}>
                          <MapPin size={13} color={C.amber} /> {villa.location_name}
                        </div>
                        <p style={{ color: C.gray500, fontSize: 13, lineHeight: 1.7, marginBottom: 14, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{villa.description}</p>
                        <div style={{ display: 'flex', gap: 18, color: C.gray600, fontSize: 13, marginBottom: 16 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Bed size={14} /> {villa.bedrooms}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Bath size={14} /> {villa.bathrooms}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Users size={14} /> {villa.max_guests}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 14, borderTop: `1px solid ${C.gray100}` }}>
                          <div>
                            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 23, fontWeight: 700, color: C.amber }}>{price.currency} {price.amount}</div>
                            <div style={{ fontSize: 11, color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.1em' }}>per {price.period}</div>
                          </div>
                          <BtnPrimary style={{ padding: '10px 18px', fontSize: 13 }}>
                            <Eye size={14} /> View Details
                          </BtnPrimary>
                        </div>
                      </div>
                    </div>
                  </Anim>
                );
              })}
            </div>
          )}

          <Anim delay={300}>
            <div style={{ textAlign: 'center', marginTop: 56 }}>
              <BtnPrimary onClick={() => window.location.href = '/villas'} style={{ padding: '16px 34px', fontSize: 16 }}>
                <Home size={18} /> View All Properties <ArrowRight size={18} />
              </BtnPrimary>
            </div>
          </Anim>
        </div>
      </section>

      {/* ══ DESTINATIONS ══ */}
      <section id="destinations" style={{ padding: '112px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="350px" color="radial-gradient(circle, rgba(217,119,6,0.08), transparent)" top="20%" left="-5%" delay={1} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>

          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <SectionLabel text="Explore Bali" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(30px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 10 }}>
                Popular Destinations
              </h2>
              <p style={{ fontSize: 17, color: C.gray500 }}>Ubud, Canggu, Seminyak — discover every corner of the island</p>
            </div>
          </Anim>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px,1fr))', gap: 24 }}>
            {destinations.map((d, i) => {
              const hov = hoveredDest === d.id;
              return (
                <Anim key={d.id} delay={i * 90}>
                  <div className="dest-card" onClick={() => window.location.href = `/villas?location=${d.id}`}
                    onMouseEnter={() => setHoveredDest(d.id)}
                    onMouseLeave={() => setHoveredDest(null)}
                    style={{ position: 'relative', height: 320, borderRadius: 24, cursor: 'pointer' }}>

                    <img className="dest-img" src={d.image} alt={d.name} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0, borderRadius: 24 }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.12) 55%, transparent)', borderRadius: 24 }} />

                    <div style={{ position: 'absolute', top: 16, right: 16, width: 42, height: 42, backgroundColor: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1)', transform: hov ? 'scale(1.25) rotate(12deg)' : 'scale(1)' }}>
                      {d.emoji}
                    </div>

                    <div style={{ position: 'absolute', bottom: 0, left: 0, padding: 26, color: C.white }}>
                      <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 38, fontWeight: 700, marginBottom: 4 }}>{d.name}</h3>
                      <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.82)', display: 'flex', alignItems: 'center', gap: 8, transform: hov ? 'translateX(8px)' : 'translateX(0)', transition: 'transform 0.4s ease' }}>
                        {d.desc}
                        <ArrowRight size={14} style={{ opacity: hov ? 1 : 0, transform: hov ? 'translateX(0)' : 'translateX(-10px)', transition: 'all 0.4s ease' }} />
                      </p>
                    </div>

                    <div style={{ position: 'absolute', inset: 0, borderRadius: 24, background: 'radial-gradient(circle at center, rgba(217,119,6,0.22), transparent 70%)', opacity: hov ? 1 : 0, transition: 'opacity 0.5s' }} />
                  </div>
                </Anim>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ SERVICES ══ */}
      <section id="services" style={{ padding: '112px 24px', backgroundColor: C.stone50, position: 'relative', overflow: 'hidden' }}>
        <Orb size="450px" color="radial-gradient(circle, rgba(217,119,6,0.08), transparent)" top="40%" left="65%" delay={0} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>

          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <SectionLabel text="What We Offer" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(30px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 10 }}>
                Premium Services
              </h2>
              <p style={{ fontSize: 17, color: C.gray500 }}>Everything you need for a perfect Bali experience</p>
            </div>
          </Anim>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px,1fr))', gap: 32 }}>
            {services.map((svc, i) => (
              <Anim key={svc.id} delay={i * 130}>
                <div className="service-card" style={{ backgroundColor: C.white, borderRadius: 20, overflow: 'hidden', boxShadow: '0 6px 28px rgba(0,0,0,0.08)', border: `1px solid ${C.gray100}` }}>
                  <div style={{ position: 'relative', height: 220, overflow: 'hidden' }}>
                    <img className="svc-img" src={svc.image_path} alt={svc.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    {svc.featured === 1 && (
                      <span style={{ position: 'absolute', top: 14, left: 14, padding: '5px 14px', backgroundColor: C.amber, color: C.white, borderRadius: 50, fontSize: 12, fontWeight: 700 }}>⭐ Popular</span>
                    )}
                  </div>
                  <div style={{ padding: 24 }}>
                    <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 23, fontWeight: 700, color: C.gray900, marginBottom: 8 }}>{svc.title}</h3>
                    <p style={{ fontSize: 14, color: C.gray500, lineHeight: 1.7, marginBottom: 18 }}>{svc.description}</p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 14, borderTop: `1px solid ${C.gray100}` }}>
                      <div>
                        <div style={{ fontWeight: 700, color: C.amber, fontSize: 18 }}>IDR {new Intl.NumberFormat('id-ID').format(svc.price_idr)}</div>
                        <div style={{ fontSize: 11, color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Starting from</div>
                      </div>
                      <BtnPrimary onClick={() => window.open(`https://wa.me/${svc.phone}`, '_blank')} style={{ padding: '10px 18px', fontSize: 13 }}>
                        <MessageCircle size={14} /> WhatsApp Us
                      </BtnPrimary>
                    </div>
                  </div>
                </div>
              </Anim>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHY CHOOSE US ══ */}
      <section style={{ padding: '112px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden' }}>
        <Orb size="600px" color="radial-gradient(circle, rgba(251,191,36,0.07), transparent)" top="-10%" left="-8%" delay={4} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>

          <Anim delay={0}>
            <div style={{ textAlign: 'center', marginBottom: 64 }}>
              <SectionLabel text="Why Dewanta" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(30px,5vw,50px)', fontWeight: 700, color: C.gray900, marginBottom: 10 }}>
                Your Trusted Local Partner
              </h2>
              <p style={{ fontSize: 17, color: C.gray500 }}>For villa management excellence in Bali</p>
            </div>
          </Anim>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px,1fr))', gap: 12, marginBottom: 96 }}>
            {[
              { emoji: '🏝️', title: 'Born & Raised in Bali', desc: 'Deep understanding of local market dynamics, regulations, and guest preferences.' },
              { emoji: '📈', title: 'Measurable Results',    desc: '25% more bookings, 40% higher occupancy, and 95% guest satisfaction.' },
              { emoji: '⭐', title: 'Experienced Team',      desc: 'Certified managers, trained hospitality staff, and 24/7 support.' },
              { emoji: '💻', title: 'Smart Management',      desc: 'Advanced booking systems, auto-pricing tools, and real-time reporting.' },
            ].map((item, i) => (
              <Anim key={i} delay={i * 100}>
                <div className="why-card" style={{ textAlign: 'center', cursor: 'default' }}>
                  <div className="why-icon" style={{ width: 66, height: 66, backgroundColor: C.amberLight, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', fontSize: 28 }}>
                    {item.emoji}
                  </div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 19, fontWeight: 700, color: C.gray900, marginBottom: 8 }}>{item.title}</h3>
                  <p style={{ fontSize: 13, color: C.gray500, lineHeight: 1.7 }}>{item.desc}</p>
                </div>
              </Anim>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px,1fr))', gap: 80, alignItems: 'center' }}>
            <div>
              <Anim dir="left" delay={0}>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px,4vw,46px)', fontWeight: 700, color: C.gray900, lineHeight: 1.15, marginBottom: 18 }}>
                  Comprehensive Villa<br /><span style={{ color: C.amber }}>Management Solutions</span>
                </h2>
                <p style={{ fontSize: 16, color: C.gray500, lineHeight: 1.8, marginBottom: 30 }}>
                  Turn your property into a luxurious investment. Choose Dewanta Property for superior management and exceptional ROI.
                </p>
              </Anim>
              <Anim dir="left" delay={130}>
                <div style={{ marginBottom: 34 }}>
                  {[
                    { title: '24/7 Guest Support',               desc: 'Round-the-clock guest communication for full assistance during their stay' },
                    { title: 'Professional Maintenance',          desc: 'Dedicated team ensuring your villa is always in excellent condition' },
                    { title: 'Connected to 15+ Global Platforms', desc: 'Maximum visibility across Airbnb, Booking.com, Agoda, and more' },
                    { title: 'Dynamic Pricing System',            desc: 'Market-optimized rate management for maximum ROI' },
                  ].map((item, i) => (
                    <div key={i} className="feat-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 10, cursor: 'default' }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: C.amber, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                        <span style={{ color: C.white, fontSize: 11, fontWeight: 700 }}>✓</span>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: C.gray900, marginBottom: 2 }}>{item.title}</div>
                        <div style={{ fontSize: 13, color: C.gray500, lineHeight: 1.6 }}>{item.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Anim>
              <Anim dir="left" delay={260}>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  <BtnPrimary onClick={() => window.location.href = '/business'}>
                    <BookOpen size={17} /> Learn More
                  </BtnPrimary>
                  <BtnOutline onClick={downloadKit}>
                    <Download size={17} /> Marketing Kit
                  </BtnOutline>
                </div>
              </Anim>
            </div>

            <Anim dir="right" delay={200}>
              <div style={{ position: 'relative' }}>
                <img src="/images/bawah.jpg" alt="Luxury Villa" style={{ width: '100%', borderRadius: 24, boxShadow: '0 30px 80px rgba(0,0,0,0.18)', display: 'block', transition: 'transform 0.8s ease' }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
                <div style={{ position: 'absolute', top: -14, right: -14, width: 80, height: 80, border: `4px solid ${C.amber}`, borderRadius: 18, opacity: 0.3, zIndex: -1, animation: 'floatOrb 5s ease-in-out infinite alternate' }} />
                <div style={{ position: 'absolute', bottom: -12, left: -12, width: 54, height: 54, backgroundColor: C.amberLight, borderRadius: 14, zIndex: -1, animation: 'floatOrb 7s ease-in-out infinite alternate', animationDelay: '2s' }} />
              </div>
            </Anim>
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.gray900, position: 'relative', overflow: 'hidden' }}>
        <Orb size="500px" color="radial-gradient(circle, rgba(217,119,6,0.22), transparent)" top="0%"  left="48%" delay={0} />
        <Orb size="300px" color="radial-gradient(circle, rgba(251,191,36,0.12), transparent)"  top="50%" left="-4%" delay={3} />
        <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Anim delay={0}>
            <p style={{ color: C.amber, fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 22 }}>Ready to Start?</p>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(34px,6vw,62px)', fontWeight: 700, color: C.white, lineHeight: 1.1, marginBottom: 18 }}>
              Let's Maximize Your<br /><span className="shimmer-text">Villa's Potential</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 16, marginBottom: 42, lineHeight: 1.7 }}>
              Join 50+ villa owners who trust Dewanta to manage their properties professionally.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <BtnPrimary onClick={() => window.location.href = '/contact'} style={{ padding: '15px 32px', fontSize: 16 }}>
                <Send size={18} /> Get Started Today
              </BtnPrimary>
              <BtnOutline onClick={() => window.location.href = '/villas'} dark style={{ padding: '14px 32px', fontSize: 16 }}>
                <Search size={18} /> Browse Villas
              </BtnOutline>
            </div>
          </Anim>
        </div>
      </section>

      
    </div>
    </MainLayout>
  );

};

export default HomePage;